/**
 * A desktop right-click menu: opens at the pointer (kept inside the window), arrow keys move, Enter runs, Esc or a
 * click outside closes; items show their shortcut so the menu also teaches the keyboard.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { SHORTCUTS } from './shortcuts';

export type MenuItem =
  | { label: string; icon?: ReactNode; keys?: string; danger?: boolean; disabled?: boolean; run: () => void }
  | { label: string; icon?: ReactNode; items: MenuItem[] }
  | 'sep';

export function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: MenuItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });
  const [active, setActive] = useState(-1);
  const [sub, setSub] = useState<number | null>(null);
  const actionable = items.map((it, i) => (it !== 'sep' && !('disabled' in it && it.disabled) ? i : -1)).filter((i) => i >= 0);

  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ x: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)), y: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)) });
    el.focus();
  }, [x, y]);
  useEffect(() => {
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const blur = () => onClose();
    window.addEventListener('mousedown', away, true); window.addEventListener('blur', blur); window.addEventListener('resize', blur);
    return () => { window.removeEventListener('mousedown', away, true); window.removeEventListener('blur', blur); window.removeEventListener('resize', blur); };
  }, [onClose]);

  const runAt = (i: number) => {
    const it = items[i]; if (!it || it === 'sep') return;
    if ('items' in it) { setSub(i); return; }
    if (it.disabled) return;
    onClose(); it.run();
  };
  const onKey = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Escape') { e.preventDefault(); if (sub != null) setSub(null); else onClose(); return; }
    if (sub != null) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const k = actionable.indexOf(active);
      const n = e.key === 'ArrowDown' ? actionable[(k + 1) % actionable.length] : actionable[(k - 1 + actionable.length) % actionable.length];
      setActive(n ?? -1);
    }
    if ((e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') && active >= 0) { e.preventDefault(); runAt(active); }
  };

  return (
    <div ref={ref} role="menu" tabIndex={-1} onKeyDown={onKey} onContextMenu={(e) => e.preventDefault()}
      className="fixed z-[1200] min-w-[220px] rounded-xl border border-[#2a2235] bg-[#1d1726] p-1 text-[12.5px] text-[#e9e3f3] shadow-[0_16px_40px_rgba(0,0,0,0.45)] outline-none"
      style={{ left: pos.x, top: pos.y }}>
      {items.map((it, i) => {
        if (it === 'sep') return <div key={`sep-${i}`} className="my-1 h-px bg-[#2a2235]" />;
        const isSub = 'items' in it;
        const disabled = !isSub && it.disabled;
        return (
          <div key={it.label} className="relative" onMouseEnter={() => { setActive(i); setSub(isSub ? i : null); }}>
            <button type="button" role="menuitem" disabled={disabled} onClick={() => runAt(i)}
              className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left ${active === i && !disabled ? 'bg-white/10' : ''} ${!isSub && it.danger ? 'text-[#ff9a8f]' : ''} disabled:opacity-35`}>
              <span className="grid w-4 shrink-0 place-items-center text-[#b9b2c6]">{it.icon}</span>
              <span className="flex-1">{it.label}</span>
              {!isSub && it.keys && <kbd className="font-sans text-[11px] text-[#7d7489]">{it.keys}</kbd>}
              {isSub && <ChevronRight className="h-3.5 w-3.5 text-[#7d7489]" />}
            </button>
            {isSub && sub === i && (
              <div className="absolute left-full top-0 ml-1 min-w-[160px] rounded-xl border border-[#2a2235] bg-[#1d1726] p-1 shadow-[0_16px_40px_rgba(0,0,0,0.45)]">
                {it.items.map((s, k) => s === 'sep' ? <div key={`s-${k}`} className="my-1 h-px bg-[#2a2235]" /> : 'items' in s ? null : (
                  <button key={s.label} type="button" role="menuitem" disabled={s.disabled} onClick={() => { onClose(); s.run(); }}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left hover:bg-white/10 disabled:opacity-35">
                    <span className="grid w-4 shrink-0 place-items-center text-[#b9b2c6]">{s.icon}</span><span className="flex-1">{s.label}</span>{s.keys && <kbd className="font-sans text-[11px] text-[#7d7489]">{s.keys}</kbd>}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ShortcutSheet({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape' || e.key === '?' || (e.key === '/' && e.shiftKey)) { e.preventDefault(); e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" onClick={onClose} className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-6">
      <div onClick={(e) => e.stopPropagation()} className="grid w-full max-w-[760px] grid-cols-1 gap-x-8 gap-y-5 rounded-2xl bg-[#1d1726] p-6 text-[#e9e3f3] shadow-2xl sm:grid-cols-2">
        <div className="col-span-full flex items-center justify-between"><span className="text-[15px] font-semibold">Keyboard shortcuts</span><span className="text-[12px] text-[#7d7489]">Esc to close</span></div>
        {SHORTCUTS.map((g) => (
          <div key={g.group} className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#a9f600]">{g.group}</span>
            {g.items.map(([k, v]) => <div key={k} className="flex items-center justify-between gap-3 text-[12.5px]"><span className="text-[#cfc8db]">{v}</span><kbd className="shrink-0 rounded-md border border-[#3a3147] bg-[#241c2e] px-1.5 py-0.5 font-sans text-[11px] text-[#e9e3f3]">{k}</kbd></div>)}
          </div>
        ))}
      </div>
    </div>
  );
}
